/**
 * 任务监控 (Admin) — 任务列表与筛选
 */

import { authFetch } from './api.js';
import { setJobsCurrentPage } from './state.js';
import { showToast, formatDate, renderPagination } from './utils.js';
import { registerViewLoader } from './navigation.js';
import { getDashboardContext } from './state.js';
import { resolveDashboardContext, getPeriodDateRange, formatRangeHint } from './dashboard-context.js';
import { readScopedQuery, writeScopedQuery } from './url-state.js';
import { withTableLoading } from './view-state.js';

const JOBS_QUERY_SCHEMA = {
  page: { type: 'int', defaultValue: 1 },
  type: { type: 'string', defaultValue: '' },
  status: { type: 'string', defaultValue: '' },
  search: { type: 'string', defaultValue: '' },
};

export function initJobs() {
  hydrateJobsFiltersFromUrl();
  registerViewLoader('admin-jobs', (page) => loadAdminJobs(page));

  const filterBtn = document.getElementById('filter-jobs-btn');
  if (filterBtn) filterBtn.addEventListener('click', () => loadAdminJobs(1));
  const searchInput = document.getElementById('search-jobs');
  if (searchInput) {
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') loadAdminJobs(1);
    });
  }
  bindDiagnosticsEvents();
}

function hydrateJobsFiltersFromUrl() {
  const state = readScopedQuery('jobs', JOBS_QUERY_SCHEMA);
  const typeSelect = document.getElementById('filter-job-type');
  const statusSelect = document.getElementById('filter-job-status');
  const searchInput = document.getElementById('search-jobs');
  if (typeSelect) typeSelect.value = state.type || '';
  if (statusSelect) statusSelect.value = state.status || '';
  if (searchInput && state.search) searchInput.value = state.search;
}

export async function loadAdminJobs(page = null) {
  const queryState = readScopedQuery('jobs', JOBS_QUERY_SCHEMA);
  const targetPage = page == null ? queryState.page : page;
  setJobsCurrentPage(targetPage);
  const type = document.getElementById('filter-job-type')?.value || '';
  const status = document.getElementById('filter-job-status')?.value || '';
  const search = document.getElementById('search-jobs')?.value?.trim() || '';
  const tbody = document.getElementById('jobs-tbody');
  writeScopedQuery('jobs', { page: targetPage, type, status, search });

  const data = await withTableLoading({
    tbody,
    colSpan: 8,
    page: targetPage,
    errorMessage: '加载任务日志失败',
    onRetry: (p) => loadAdminJobs(p),
    onError: (error) => {
      console.error('Error loading jobs:', error);
      showToast('加载任务日志失败', 'error');
    },
    task: async () => {
      const ctx = getDashboardContext() || resolveDashboardContext() || getPeriodDateRange('month');
      const hint = document.getElementById('jobs-range-hint');
      if (hint) hint.textContent = '统计范围：' + formatRangeHint(ctx);
      const params = new URLSearchParams({
        page: targetPage, size: 20,
        startDate: ctx.startDate,
        endDate: ctx.endDate,
        ...(status && { status }),
        ...(search && { search }),
      });
      if (ctx.groupId) params.set('groupId', ctx.groupId);

      const response = await authFetch(`/api/admin/jobs?${params}`);
      if (!response.ok) throw new Error('Failed to load jobs');
      return response.json();
    },
  });
  if (!data) return;
  const filteredJobs = filterJobsByType(data.jobs || [], type);
  renderJobsTable(filteredJobs, type ? filteredJobs.length : data.total, targetPage, 20);
}

function createJobsElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = String(text);
  return element;
}

function createJobsTableEmptyRow(message) {
  const state = createJobsElement('div', 'empty-state');
  state.append(
    createJobsElement('div', 'empty-icon', '📋'),
    createJobsElement('p', '', message),
  );
  const cell = document.createElement('td');
  cell.colSpan = 8;
  cell.appendChild(state);
  const row = document.createElement('tr');
  row.appendChild(cell);
  return row;
}

function createJobRow(job, statusLabels) {
  const st = statusLabels[job.status] || { text: job.status || '-', cls: '' };
  const taskType = getJobType(job);
  const createdAt = formatDate(job.createdAt);
  const completedAt = getCompletedAt(job);
  const durationText = formatJobDuration(job);
  const phaseText = formatJobPhase(job.phase);
  const marketplaceText = String(job.marketplace || '-');
  const userName = job.userName || job.userEmail || '-';
  const row = document.createElement('tr');

  const idCell = document.createElement('td');
  const idWrap = createJobsElement('div', 'jobs-id-cell');
  const idMain = createJobsElement('div', 'jobs-id-main mono', job.id || '-');
  idMain.title = String(job.id || '-');
  idWrap.append(
    idMain,
    createJobsElement('div', 'jobs-id-sub', `站点 ${marketplaceText}`),
  );
  idCell.appendChild(idWrap);
  row.appendChild(idCell);

  const typeCell = document.createElement('td');
  typeCell.append(
    createJobsElement('div', 'jobs-type-label', taskType.label),
    createJobsElement('div', 'jobs-type-sub', phaseText),
  );
  row.appendChild(typeCell);

  const statusCell = document.createElement('td');
  statusCell.appendChild(createJobsElement('span', `badge ${st.cls}`.trim(), st.text));
  if (job.error) {
    const note = createJobsElement('div', 'jobs-status-note jobs-status-note-error', job.error.substring(0, 56));
    note.title = String(job.error);
    if (job.error.length > 56) note.appendChild(document.createTextNode('...'));
    statusCell.appendChild(note);
  } else {
    statusCell.appendChild(createJobsElement('div', 'jobs-status-note', `阶段 ${phaseText}`));
  }
  row.appendChild(statusCell);

  const userCell = document.createElement('td');
  userCell.appendChild(createJobsElement('div', 'jobs-user-name', userName));
  row.appendChild(userCell);
  row.appendChild(createJobsElement('td', 'mono jobs-time-cell', createdAt));
  row.appendChild(createJobsElement('td', 'mono jobs-time-cell', completedAt));
  row.appendChild(createJobsElement('td', 'mono jobs-duration-cell', durationText));

  const actionCell = document.createElement('td');
  const actions = createJobsElement('div', 'jobs-actions');
  const diagnosticsButton = createJobsElement('button', 'btn btn-secondary btn-sm jobs-diag-btn', '查看诊断');
  diagnosticsButton.type = 'button';
  diagnosticsButton.dataset.jobId = String(job.id ?? '');
  actions.appendChild(diagnosticsButton);
  actionCell.appendChild(actions);
  row.appendChild(actionCell);
  return row;
}

function renderJobsTable(jobs, total, page, size) {
  const tbody = document.getElementById('jobs-tbody');
  if (!tbody) return;
  renderJobsPageMeta(total, page, size);

  if (!jobs || jobs.length === 0) {
    tbody.replaceChildren(createJobsTableEmptyRow('暂无任务数据'));
    renderPagination('jobs-pagination', total, page, size, (p) => loadAdminJobs(p));
    return;
  }

  const statusLabels = {
    queued: { text: '排队中', cls: 'badge-member' },
    running: { text: '运行中', cls: 'badge-running' },
    completed: { text: '已完成', cls: 'badge-active' },
    failed: { text: '失败', cls: 'badge-disabled' },
  };
  tbody.replaceChildren(...jobs.map((job) => createJobRow(job, statusLabels)));

  renderPagination('jobs-pagination', total, page, size, (p) => loadAdminJobs(p));
}

function filterJobsByType(jobs, selectedType) {
  if (!selectedType) return jobs;
  return jobs.filter((job) => getJobType(job).key === selectedType);
}

function getJobType(job) {
  return { key: 'batch', label: '批量分析' };
}

function formatJobPhase(phase) {
  if (!phase) return '未分配阶段';
  const map = {
    scraping: '抓取中',
    building_keywords: '关键词处理中',
    ready: '已就绪',
    completed: '已完成',
    failed: '执行失败',
  };
  return map[phase] || String(phase).replace(/_/g, ' ');
}

function getCompletedAt(job) {
  if (!job) return '--';
  if (job.status === 'completed' || job.status === 'failed') {
    return formatDate(job.updatedAt);
  }
  return '--';
}

function formatJobDuration(job) {
  const createdAtMs = Date.parse(job?.createdAt || '');
  if (!Number.isFinite(createdAtMs)) return '--';
  if (job?.status === 'queued') return '--';
  const endMs = job?.status === 'running'
    ? Date.now()
    : Date.parse(job?.updatedAt || '');
  if (!Number.isFinite(endMs) || endMs < createdAtMs) return '--';
  const diff = endMs - createdAtMs;
  const text = formatDurationMs(diff);
  return job?.status === 'running' ? `${text}...` : text;
}

function formatDurationMs(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '--';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

function renderJobsPageMeta(total, page, size) {
  const el = document.getElementById('jobs-page-meta');
  if (!el) return;
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / size));
  el.textContent = `第 ${page} 页 / 共 ${totalPages} 页`;
}

function bindDiagnosticsEvents() {
  const tbody = document.getElementById('jobs-tbody');
  const overlay = document.getElementById('jobs-diagnostics-overlay');
  const closeBtn = document.getElementById('jobs-diagnostics-close');
  if (closeBtn) closeBtn.addEventListener('click', closeDiagnostics);
  if (overlay) {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeDiagnostics();
    });
  }
  if (!tbody || tbody.dataset.diagBound === '1') return;
  tbody.dataset.diagBound = '1';
  tbody.addEventListener('click', async (e) => {
    const btn = e.target.closest('.jobs-diag-btn');
    if (!btn) return;
    const jobId = btn.dataset.jobId;
    if (!jobId) return;
    await openDiagnostics(jobId);
  });
}

function closeDiagnostics() {
  const overlay = document.getElementById('jobs-diagnostics-overlay');
  if (overlay) overlay.hidden = true;
}

function createDiagnosticsStats(entries, emptyText) {
  const stats = createJobsElement('div', 'jobs-diagnostics-stats');
  const rows = entries.length ? entries : [[emptyText, 0]];
  stats.replaceChildren(...rows.map(([label, value]) => {
    const stat = createJobsElement('div', 'jobs-diagnostics-stat');
    stat.append(
      createJobsElement('span', 'jobs-diagnostics-stat-label', label),
      createJobsElement('strong', 'jobs-diagnostics-stat-value', Number(value || 0)),
    );
    return stat;
  }));
  return stats;
}

function createDiagnosticsCard(title, content) {
  const card = createJobsElement('div', 'jobs-diagnostics-card');
  card.appendChild(createJobsElement('h4', '', title));
  card.appendChild(content);
  return card;
}

function createDiagnosticsTable(headers, rows, emptyText) {
  const wrap = createJobsElement('div', 'jobs-diagnostics-table-wrap');
  const table = createJobsElement('table', 'data-table jobs-diagnostics-table');
  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  headers.forEach((header) => headRow.appendChild(createJobsElement('th', '', header)));
  head.appendChild(headRow);
  const body = document.createElement('tbody');
  if (!rows.length) {
    const emptyCell = createJobsElement('td', '', emptyText);
    emptyCell.colSpan = headers.length;
    const emptyRow = document.createElement('tr');
    emptyRow.appendChild(emptyCell);
    body.appendChild(emptyRow);
  } else {
    body.replaceChildren(...rows.map((values) => {
      const row = document.createElement('tr');
      values.forEach((value) => row.appendChild(createJobsElement('td', '', value)));
      return row;
    }));
  }
  table.append(head, body);
  wrap.appendChild(table);
  return wrap;
}

async function openDiagnostics(jobId) {
  const overlay = document.getElementById('jobs-diagnostics-overlay');
  const titleEl = document.getElementById('jobs-diagnostics-title');
  const bodyEl = document.getElementById('jobs-diagnostics-body');
  if (!overlay || !titleEl || !bodyEl) return;

  overlay.hidden = false;
  titleEl.textContent = `任务诊断 · ${jobId}`;
  bodyEl.replaceChildren(createJobsElement('div', 'jobs-diagnostics-loading', '加载中...'));
  try {
    const res = await authFetch(`/api/admin/task-diagnostics?jobId=${encodeURIComponent(jobId)}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    const byStatus = data?.imageChain?.byStatus || {};
    const byProvider = data?.imageChain?.byProvider || {};
    const recent = Array.isArray(data?.imageChain?.recent) ? data.imageChain.recent.slice(0, 8) : [];
    const operations = data?.operations || {};
    const timeout = operations.timeoutPolicy || {};
    const recovery = operations.recovery || {};
    const grid = createJobsElement('div', 'jobs-diagnostics-grid');
    grid.append(
      createDiagnosticsCard('图片状态统计', createDiagnosticsStats(Object.entries(byStatus), '暂无状态统计')),
      createDiagnosticsCard('Provider 统计', createDiagnosticsStats(Object.entries(byProvider), '暂无 provider 统计')),
    );
    const queueRows = Array.isArray(operations.queues)
      ? operations.queues.map((queue) => [
        queue.label || queue.key || '',
        queue.status || '',
        Number(queue.running || 0),
        Number(queue.waiting || 0),
        queue.diagnostic || '',
      ])
      : [];
    const queueCard = createDiagnosticsCard(
      '队列、超时与恢复',
      createDiagnosticsTable(['链路', '状态', '运行中', '排队中', '诊断源'], queueRows, '暂无队列诊断'),
    );
    const note = createJobsElement('div', 'jobs-diagnostics-note');
    note.append(
      document.createTextNode('Workspace stale: '),
      document.createTextNode(String(timeout.workspaceStaleMs || '-')),
      document.createTextNode('ms；恢复建议：'),
      document.createTextNode(String(recovery.reason || '暂无')),
    );
    queueCard.appendChild(note);
    const recentRows = recent.map((record) => [
      String(record.id || ''),
      record.provider || 'unknown',
      record.model || '',
      record.status || '',
    ]);
    const recentCard = createDiagnosticsCard(
      '最近图片记录（含 provider/model）',
      createDiagnosticsTable(['ID', 'Provider', 'Model', '状态'], recentRows, '暂无最近图片记录'),
    );
    bodyEl.replaceChildren(grid, queueCard, recentCard);
  } catch (err) {
    bodyEl.replaceChildren(createJobsElement('div', 'jobs-diagnostics-error', `加载失败: ${err.message || '未知错误'}`));
  }
}
